import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useProject } from "@/hooks/useProjects";
import { useCustomers } from "@/hooks/useCustomers";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { useProjectWorkOrders } from "@/hooks/useProjectContracts";
import {
  useProjectChangeOrders,
  useProjectInvoices,
  useProjectJobOrders,
} from "@/hooks/useProjectOrders";
import { useProjectFinancials } from "@/hooks/useProjectFinancials";
import { usePermissions } from "@/hooks/usePermissions";
import { supabase } from "@/integrations/supabase/client";
import { useProjectAlerts } from "@/hooks/useProjectAlerts";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Pencil,
  Printer,
  Users,
  FileText,
  Wrench,
  Copy,
  ClipboardCopy,
  Lock,
} from "lucide-react";
import { exportPdf } from "@/lib/export";
import { ProjectSummaryPrintTemplate } from "@/components/shared/print-templates/ProjectSummaryPrintTemplate";
import { PageActions } from "@/components/shared/PageActions";
import { ProjectTeamPanel } from "@/components/controller/project-detail/ProjectTeamPanel";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    n
  );
import { ProjectKPICards } from "@/components/controller/project-detail/ProjectKPICards";
import { ProjectOverviewTab } from "@/components/controller/project-detail/ProjectOverviewTab";
import { ProjectContractTab } from "@/components/controller/project-detail/ProjectContractTab";
import { ProjectSiteInfoTab } from "@/components/controller/project-detail/ProjectSiteInfoTab";
import { ProjectWOTab } from "@/components/controller/project-detail/ProjectWOTab";
import { ProjectCOTab } from "@/components/controller/project-detail/ProjectCOTab";
import { ProjectInvoicesTab } from "@/components/controller/project-detail/ProjectInvoicesTab";
import { ProjectUnitsTab } from "@/components/controller/project-detail/ProjectUnitsTab";
import { UnitCompletionReview } from "@/components/controller/project-detail/UnitCompletionReview";
import { RetainageDashboardCard } from "@/components/controller/project-detail/RetainageDashboardCard";
import { AdjustmentsDashboardCard } from "@/components/controller/project-detail/AdjustmentsDashboardCard";
import { EditProjectDialog } from "@/components/controller/EditProjectDialog";
import { ProjectCertifiedPayrollTab } from "@/components/controller/project-detail/ProjectCertifiedPayrollTab";
import { ProjectExpensesTab } from "@/components/controller/project-detail/ProjectExpensesTab";
import { ProjectAlertsBanner } from "@/components/controller/project-detail/ProjectAlertsBanner";
import { ProjectCommsTab } from "@/components/controller/project-detail/ProjectCommsTab";
import type { Database } from "@/integrations/supabase/types";

type ProjectDetailRecord = Database["public"]["Tables"]["projects"]["Row"] & {
  customers?: { name: string | null } | null;
  project_assignments?: { user_id: string | null }[] | null;
  personnel_project_id?: string | null;
};

type ProjectChangeOrderRecord =
  Database["public"]["Tables"]["change_orders"]["Row"] & {
    notes?: string | null;
    job_orders?: { number: string } | null;
    work_orders?: { number: string } | null;
  };

type ProjectJobOrderRecord = Database["public"]["Tables"]["job_orders"]["Row"];
type ProjectWorkOrderRecord = Pick<
  Database["public"]["Tables"]["work_orders"]["Row"],
  "id" | "number"
>;

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, isAdmin } = usePermissions();
  const [editOpen, setEditOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("contract");
  const [closeoutBusy, setCloseoutBusy] = useState(false);
  const [closeoutBlockers, setCloseoutBlockers] = useState<string[] | null>(null);
  const { data: project, isLoading } = useProject(id);
  const { data: companyProfile } = useCompanyProfile();
  const { data: cos } = useProjectChangeOrders(id);
  const { data: invoices } = useProjectInvoices(id);
  const { data: jobOrders } = useProjectJobOrders(id);
  const { data: customers } = useCustomers();
  const { data: workOrders } = useProjectWorkOrders(id);
  const financials = useProjectFinancials(id);
  const { alerts, sovAlertMap } = useProjectAlerts(id);

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (!project) return <p className="text-destructive">Project not found.</p>;

  const projectRecord = project as ProjectDetailRecord;
  const changeOrders = (cos ?? []) as ProjectChangeOrderRecord[];
  const projectJobOrders = (jobOrders ?? []) as ProjectJobOrderRecord[];
  const projectWorkOrders = (workOrders ?? []) as ProjectWorkOrderRecord[];

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["change_orders", id] });
    qc.invalidateQueries({ queryKey: ["invoices", "project", id] });
    qc.invalidateQueries({ queryKey: ["contracts", id] });
    qc.invalidateQueries({ queryKey: ["sov_lines"] });
    qc.invalidateQueries({ queryKey: ["work_orders", id] });
    qc.invalidateQueries({ queryKey: ["work_order_lines", id] });
    qc.invalidateQueries({ queryKey: ["project_contract_value", id] });
    qc.invalidateQueries({ queryKey: ["project_committed_cost", id] });
    qc.invalidateQueries({ queryKey: ["project_actual_cost", id] });
    qc.invalidateQueries({ queryKey: ["project_estimated_cost", id] });
    qc.invalidateQueries({ queryKey: ["project_billed_to_date", id] });
    qc.invalidateQueries({ queryKey: ["sov_line_financials"] });
    qc.invalidateQueries({ queryKey: ["dashboard_metrics"] });
    qc.invalidateQueries({ queryKey: ["approval_counts"] });
    qc.invalidateQueries({ queryKey: ["project_variance_drivers", id] });
  };

  const handlePrint = () =>
    exportPdf(
      <ProjectSummaryPrintTemplate
        data={{
          name: project.name,
          project_number: project.project_number,
          status: project.status,
          customer_name: project.customers?.name,
          city: project.city,
          state: project.state,
          company_name: companyProfile?.name,
          company_logo_url: companyProfile?.logo_url,
          created_at: project.created_at,
          kpis: [
            { label: "Contract Value", value: fmt(financials.contractValue) },
            { label: "Estimated Cost", value: fmt(financials.estimatedCost) },
            { label: "Committed Cost", value: fmt(financials.committedCost) },
            { label: "Actual Cost", value: fmt(financials.actualCost) },
            { label: "Billed to Date", value: fmt(financials.billedToDate) },
            { label: "Collected", value: fmt(financials.collectedToDate) },
            {
              label: "Margin (Estimated)",
              value: `${financials.estimatedMargin.toFixed(1)}%`,
            },
            {
              label: "Margin (Committed)",
              value: `${financials.margin.toFixed(1)}%`,
            },
            {
              label: "Margin (Actual)",
              value: `${financials.actualCostMargin.toFixed(1)}%`,
            },
            {
              label: "Team Members",
              value: String(project.project_assignments?.length ?? 0),
            },
          ],
        }}
      />,
      `Project Summary - ${project.name}`
    );

  const handleCloseOut = async () => {
    setCloseoutBusy(true);
    try {
      const { data, error } = await supabase.rpc("close_project", { p_project_id: id });
      if (error) {
        if (error.message.includes("PROJECT_CLOSEOUT_BLOCKED")) {
          const blockers = error.hint?.split(",") ?? [];
          setCloseoutBlockers(blockers);
        } else {
          toast.error(error.message);
        }
      } else {
        toast.success("Project closed out successfully");
        invalidateAll();
        qc.invalidateQueries({ queryKey: ["project", id] });
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setCloseoutBusy(false);
    }
  };

  const pageActions = [
    {
      label: "Edit Project",
      icon: Pencil,
      onClick: () => setEditOpen(true),
      permission: isAdmin || can("edit_project_info"),
    },
    {
      label: "Assign Team",
      icon: Users,
      onClick: () => setTeamOpen(true),
    },
    {
      label: "Create Invoice",
      icon: FileText,
      onClick: () => setActiveTab("invoices"),
      separator: true,
    },
    {
      label: "Create Work Order",
      icon: Wrench,
      onClick: () => setActiveTab("work_orders"),
    },
    {
      label: "Close Out Project",
      icon: Lock,
      onClick: handleCloseOut,
      permission: isAdmin && project.status !== "completed",
      separator: true,
    },
    {
      label: "Print Summary",
      icon: Printer,
      onClick: handlePrint,
    },
    {
      label: "Copy Project #",
      icon: ClipboardCopy,
      onClick: () => {
        if (project.project_number) {
          navigator.clipboard.writeText(project.project_number);
          toast.success("Project number copied");
        }
      },
      permission: !!project.project_number,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/controller/projects")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                {project.name}
              </h1>
              {project.project_number && (
                <Badge className="bg-foreground text-background font-mono text-xs">
                  {project.project_number}
                </Badge>
              )}
              <StatusBadge status={project.status} />
            </div>
          </div>
          <div className="ml-12 flex items-center gap-2 text-sm text-muted-foreground">
            {project.customers?.name && <span>{project.customers.name}</span>}
            {(project.city || project.state) && (
              <span>
                · {[project.city, project.state].filter(Boolean).join(", ")}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 ml-12 md:ml-0">
          <PageActions actions={pageActions} />
        </div>
      </div>

      <EditProjectDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        project={project}
      />
      <ProjectTeamPanel
        projectId={id!}
        open={teamOpen}
        onOpenChange={setTeamOpen}
      />

      {/* KPI Cards */}
      <ProjectKPICards
        contractValue={financials.contractValue}
        estimatedCost={financials.estimatedCost}
        committedCost={financials.committedCost}
        actualCost={financials.actualCost}
        billedToDate={financials.billedToDate}
        collectedToDate={financials.collectedToDate}
        estimatedMargin={financials.estimatedMargin}
        margin={financials.margin}
        actualCostMargin={financials.actualCostMargin}
      />

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <ProjectAlertsBanner alerts={alerts} onNavigate={setActiveTab} />
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:static md:z-auto md:bg-transparent md:backdrop-blur-none">
          <TabsTrigger value="contract">Contract / SOV</TabsTrigger>
          {projectRecord.has_unit_schedule && (
            <TabsTrigger value="units">Units</TabsTrigger>
          )}
          <TabsTrigger value="site_info">Site Info</TabsTrigger>
          <TabsTrigger value="work_orders">Work Orders</TabsTrigger>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="change_orders">Change Orders</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          {projectRecord.has_unit_schedule && (
            <TabsTrigger value="billing_review">Billing Review</TabsTrigger>
          )}
          <TabsTrigger value="expenses">Expenses</TabsTrigger>
          {projectRecord.is_public_works && (
            <TabsTrigger value="certified_payroll">
              Certified Payroll
            </TabsTrigger>
          )}
          <TabsTrigger value="comms">Comms</TabsTrigger>
        </TabsList>

        <TabsContent value="contract">
          <ProjectContractTab
            projectId={id!}
            onMutate={invalidateAll}
            sovAlertMap={sovAlertMap}
          />
        </TabsContent>

        {projectRecord.has_unit_schedule && (
          <TabsContent value="units">
            <ProjectUnitsTab
              projectId={id!}
              project={project}
              onMutate={invalidateAll}
            />
          </TabsContent>
        )}

        <TabsContent value="site_info">
          <ProjectSiteInfoTab project={project} />
        </TabsContent>

        <TabsContent value="work_orders">
          <ProjectWOTab
            projectId={id!}
            project={project}
            onMutate={invalidateAll}
          />
        </TabsContent>

        <TabsContent value="overview">
          <div className="space-y-6">
            <ProjectOverviewTab
              projectId={id!}
              contractValue={financials.contractValue}
              estimatedCost={financials.estimatedCost}
              committedCost={financials.committedCost}
              actualCost={financials.actualCost}
              pendingPOs={0}
              billedToDate={financials.billedToDate}
              collectedToDate={financials.collectedToDate}
              pendingInvoices={(invoices ?? [])
                .filter((i) => i.status === "draft")
                .reduce((s, i) => s + Number(i.total), 0)}
              estimatedMargin={financials.estimatedMargin}
              margin={financials.margin}
              actualCostMargin={financials.actualCostMargin}
              teamCount={project.project_assignments?.length ?? 0}
              alerts={alerts}
            />
            <RetainageDashboardCard
              projectId={id!}
              projectName={project.name}
              projectStatus={project.status}
              retainageReleasedAt={projectRecord.retainage_released_at}
            />
            <AdjustmentsDashboardCard
              projectId={id!}
              projectName={project.name}
            />
          </div>
        </TabsContent>

        <TabsContent value="change_orders">
          <ProjectCOTab
            projectId={id!}
            project={{
              name: project.name,
              project_number: projectRecord.project_number ?? null,
              location: projectRecord.location ?? null,
              company_id: projectRecord.company_id ?? null,
            }}
            cos={changeOrders.map((c) => ({
              ...c,
              amount: Number(c.amount),
              notes: c.notes ?? null,
              work_order_id: c.work_order_id ?? null,
              work_orders: c.work_orders ?? null,
            }))}
            jos={projectJobOrders.map((jo) => ({
              id: jo.id,
              number: jo.number,
            }))}
            wos={projectWorkOrders.map((wo) => ({
              id: wo.id,
              number: wo.number,
            }))}
            onMutate={invalidateAll}
          />
        </TabsContent>

        <TabsContent value="invoices">
          <ProjectInvoicesTab
            projectId={id!}
            invoices={(invoices ?? []).map((i) => ({
              ...i,
              total: Number(i.total),
            }))}
            customers={customers ?? []}
            contractValue={financials.contractValue}
            onMutate={invalidateAll}
            project={project}
          />
        </TabsContent>

        {projectRecord.has_unit_schedule && (
          <TabsContent value="billing_review">
            <UnitCompletionReview
              projectId={id!}
              project={project}
              onMutate={invalidateAll}
            />
          </TabsContent>
        )}

        <TabsContent value="expenses">
          <ProjectExpensesTab projectId={id!} />
        </TabsContent>

        {projectRecord.is_public_works && (
          <TabsContent value="certified_payroll">
            <ProjectCertifiedPayrollTab
              projectId={id!}
              personnelProjectId={projectRecord.personnel_project_id}
              companyId={projectRecord.company_id}
            />
          </TabsContent>
        )}

        <TabsContent value="comms">
          <ProjectCommsTab projectId={id!} />
        </TabsContent>
      </Tabs>

      {/* Closeout Blockers Dialog */}
      <Dialog open={closeoutBlockers !== null} onOpenChange={() => setCloseoutBlockers(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cannot Close Project</DialogTitle>
            <DialogDescription>
              The following work orders have unbilled line items that must be resolved before closing out the project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(closeoutBlockers ?? []).map((woNum) => (
              <div key={woNum} className="flex items-center gap-2 p-2 rounded-md bg-destructive/10 text-destructive text-sm">
                <Lock className="h-4 w-4 shrink-0" />
                <span className="font-mono font-medium">{woNum}</span>
                <span className="text-muted-foreground">— has unbilled lines</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Bill remaining items or return unbilled scope back to the project SOV before closing out.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseoutBlockers(null)}>Close</Button>
            <Button onClick={() => { setCloseoutBlockers(null); setActiveTab("work_orders"); }}>
              Go to Work Orders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
